import React, { FC, useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-mini';

import { Store } from 'redux/root';
import { createChallenge, setLoadingChallenges, updateChallenge } from 'redux/challenges-service/actions';

import { IChallengeResponse } from '@joc/api-gateway';
import DialogStyled from 'components/Organization/Placeholders/PlaceholderDialog/DialogStyled';
import ButtonCross from 'shared/components/Buttons/ButtonCross';
import { useBatchFileUpload } from 'hooks/useBatchFileUpload';
import { UploadProgressSnackbar } from 'shared/components/UploadProgressSnackbar';
import ChallengeForm from './ChallengeForm';
import ChallengesPreview from '../ChallengesPreview';

import { challengeValidationSchema } from '../helpers';
import { ChallengeFormValuesType } from '../types';

import { ICreateChallengeRequest } from '@joc/api-gateway/lib/api-client';

import styles from 'components/Organization/Placeholders/PlaceholderDialog/PlaceholderDialog.module.scss';

interface ChallengeCreateModalProps {
	isModalOpen: boolean;
	// eslint-disable-next-line no-unused-vars
	onClose: (successSubmitType?: boolean) => void;
	challenge?: IChallengeResponse;
}

const ChallengeModal: FC<ChallengeCreateModalProps> = ({ isModalOpen, onClose, challenge }) => {
	const dispatch = useDispatch();
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [dataToPreview, setDataToPreview] = useState<Partial<IChallengeResponse> | null>(null);
	const isReduxLoading = useSelector((store: Store) => store.challengeRedux.isLoading);

	const { items: batchUploadItems, uploadBatch, isSubmitting: isUploadingAttachments, abort } = useBatchFileUpload();
	const isLoading = isReduxLoading || isUploadingAttachments;

	const closeDialog = () => {
		if (isUploadingAttachments) {
			abort();
		}
		onClose();
	};

	useEffect(() => {
		return () => {
			if (isUploadingAttachments) {
				abort();
			}
		};
	}, [isUploadingAttachments, abort]);

	const submitClickHandler = async (values: ChallengeFormValuesType) => {
		dispatch(setLoadingChallenges(true));

		try {
			let uploadedAttachmentKeys: string[] = [];
			if (values.pendingAttachmentFiles && values.pendingAttachmentFiles.length > 0) {
				uploadedAttachmentKeys = await uploadBatch(values.pendingAttachmentFiles);
			}

			const allAttachments = [...(values.attachments || []), ...uploadedAttachmentKeys];

			const { name, title, chesedIdeas, imagePath } = values;
			const startDate = moment(values.startDate)
				.set({
					hours: moment(values.startTime, 'HH:mm').hours(),
					minutes: moment(values.startTime, 'HH:mm').minutes(),
				})
				.toDate();

			const deadlineDate = moment(values.endDate)
				.set({
					hours: moment(values.endTime, 'HH:mm').hours(),
					minutes: moment(values.endTime, 'HH:mm').minutes(),
				})
				.toDate();

			const body = document.createElement('body');
			body.innerHTML = values.description;

			const mutatedValues: ICreateChallengeRequest = {
				name,
				title,
				description: body.outerHTML,
				startDate,
				endDate: deadlineDate,
				chesedIdeas: chesedIdeas as unknown as Array<Array<any>>,
				imagePath,
				attachments: allAttachments as unknown as Array<Array<any>>,
			};

			if (challenge) {
				dispatch(updateChallenge({ id: challenge.id, challenge: mutatedValues }));
			} else {
				dispatch(createChallenge(mutatedValues));
			}

			setIsSubmitted(true);
		} catch (error) {
			dispatch(setLoadingChallenges(false));
			setIsSubmitted(false);
		}
	};

	const handlePreviewOpen = (previewData: Partial<IChallengeResponse> | null) => setDataToPreview(previewData);

	useEffect(() => {
		if (isSubmitted && !isLoading) {
			onClose(true);
		}
	}, [isLoading, isSubmitted]);

	const initialData = useMemo<ChallengeFormValuesType>((): ChallengeFormValuesType => {
		const div = document.createElement('div');
		div.innerHTML = challenge?.description || '';

		return {
			name: challenge?.name || '',
			title: challenge?.title || '',
			description: challenge?.description ? div.innerHTML : '',
			startDate: challenge?.startDate || new Date(),
			startTime: challenge?.startDate ? moment(challenge?.startDate).format('HH:mm') : moment().format('HH:mm'),
			endDate: challenge?.endDate || new Date(),
			endTime: challenge?.endDate
				? moment(challenge?.endDate).format('HH:mm')
				: moment()
						.set({
							hours: moment().hours() + 1,
						})
						.format('HH:mm'),
			imagePath: challenge?.imagePath || '',
			chesedIdeas: challenge?.chesedIdeas.length ? challenge?.chesedIdeas : [''],
			attachments: challenge?.attachments || [],
			pendingAttachmentFiles: [],
		};
	}, [challenge]);

	return (
		<DialogStyled open={isModalOpen} maxWidth="xl">
			<section className={styles.dialog} style={{ width: 932 }}>
				<div className={styles.dialog__header}>
					<ButtonCross clickHandler={closeDialog} parentClassName={styles.dialog__header_button} />
				</div>
				{initialData ? (
					<Formik
						enableReinitialize={true}
						initialValues={initialData}
						validationSchema={challengeValidationSchema}
						onSubmit={submitClickHandler}
						className={styles.dialog__form}
					>
						<ChallengeForm isNew={!challenge} onClose={closeDialog} onPreview={handlePreviewOpen} />
					</Formik>
				) : null}

				{dataToPreview ? (
					<ChallengesPreview previewData={dataToPreview} handlePreviewOpen={setDataToPreview} />
				) : null}
			</section>
			<UploadProgressSnackbar open={isUploadingAttachments} items={batchUploadItems} />
		</DialogStyled>
	);
};

export default ChallengeModal;
